import { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import { MaybePromise } from '@reduxjs/toolkit/dist/query/tsHelpers';
import {
  paginatedQuery,
  streamReader,
  streamUpdates,
} from 'lib-shared-api-utils';
import {
  ApiErrorStatusCode,
  createAuthorizationHeaderValue,
  QueryError,
} from 'lib-shared-auth';
import { config } from 'lib-shared-backend-config';

import { api } from 'app/app.api';
import { getMimDraftFailureMessage } from 'utils/mimDraft';

import {
  CreateAiAgentsApiArg,
  CreateAiAgentsApiResponse,
  CreateAiAgentsTestVersionApiArg,
  CreateAiAgentsTestVersionResponse,
  CreateAiAgentsConfigCommitsApiArg,
  CreateAiAgentsConfigCommitsApiResponse,
  CreateAiAgentsDeploymentsApiArg,
  CreateAiAgentsDuplicateApiArg,
  CreateAiAgentsSidekickConversationApiArg,
  CreateAiAgentsSidekickConversationApiResponse,
  CreateAiAgentsSidekickConversationMessagesApiArg,
  CreateAiAgentsSidekickConversationMessagesApiResponse,
  CreateAiAgentsToolApiArg,
  CreateAiAgentsToolApiResponse,
  CreateAiAgentsToolDuplicateApiArg,
  CreateAiAgentsToolsByIdApiArg,
  CreateAiAgentsToolsByIdApiResponse,
  CreateToolsDynamicSchemaRunApiArg,
  CreateToolsDynamicSchemaRunApiResponse,
  CreateToolsIntegrationsActionsRunApiArg,
  CreateToolsIntegrationsActionsRunApiResponse,
  DeleteAiAgentsApiArg,
  DeleteAiAgentsToolApiArg,
  DeleteAiAgentsToolApiResponse,
  ReadAiAgentsApiArg,
  ReadAiAgentsApiResponse,
  ReadAiAgentsConfigCommitsApiArg,
  ReadAiAgentsConfigCommitsApiResponse,
  ReadAiAgentsDefaultsApiResponse,
  ReadAiAgentsKnowledgeBasesApiArg,
  ReadAiAgentsKnowledgeBasesApiResponse,
  ReadAiAgentsListApiArg,
  ReadAiAgentsListApiResponse,
  ReadAiAgentsSidekickConversationsApiArg,
  ReadAiAgentsSidekickConversationsApiResponse,
  ReadAiAgentsSidekickConversationsMessagesApiArg,
  ReadAiAgentsSidekickConversationsMessagesApiResponse,
  ReadAiAgentsToolApiArg,
  ReadAiAgentsToolApiResponse,
  ReadAiAgentsToolReferencesApiArg,
  ReadAiAgentsToolReferencesApiResponse,
  ReadAiAgentsToolsApiArg,
  ReadAiAgentsToolsApiResponse,
  ReadChatbotsQuotasV2ApiArg,
  ReadChatbotsQuotasV2ApiResponse,
  ReadToolsIntegrationsActionsApiArg,
  ReadToolsIntegrationsActionsApiResponse,
  UpdateAiAgentsApiArg,
  UpdateAiAgentsApiResponse,
  UpdateAiAgentsConfigCommitApiArg,
  UpdateAiAgentsConfigCommitApiResponse,
  UpdateChatbotsQuotasApiArg,
  UpdateChatbotsQuotasApiResponse,
  CreateAiAgentsVersionRestoreApiArg,
  CreateAiAgentsVersionRestoreApiResponse,
} from './types/aiAgentsApi.types';
import type {
  AiAgentDraftTarget,
  RunAiAgentRequest,
} from './types/runAiAgent.types';
import { createAiAgentMessageAppender } from './utils/createAiAgentMessageAppender';

export type AiAgentRunStreamResponse = {
  text: string;
  /** The run's exit reason when it ended in a failed status. */
  failureReason?: string;
  meta: {
    isLoading: boolean;
    isStreaming: boolean;
    isError: boolean;
    isSuccess: boolean;
  };
  feedback_id: string;
};

export const aiAgentsApi = api
  .enhanceEndpoints({
    addTagTypes: [
      'AiAgents',
      'AiAgent',
      'AiAgentTools',
      'AiAgentTool',
      'AiAgentConfigCommits',
      'ChatbotQuotas',
      'SidekickConversations',
    ],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      // AI Agent CRUD
      createAiAgents: builder.mutation<
        CreateAiAgentsApiResponse,
        CreateAiAgentsApiArg
      >({
        query: ({ name, organizationId, knowledgeBases, user, variant }) => ({
          version: 1,
          path: 'ai-agents',
          method: 'POST',
          body: {
            name,
            organizationId,
            knowledgeBases,
            user,
            variant,
          },
        }),
        transformResponse: (response: unknown): CreateAiAgentsApiResponse => ({
          data: response as CreateAiAgentsApiResponse['data'],
          meta: {},
        }),
        invalidatesTags: ['AiAgents'],
      }),
      createAiAgentsTestVersion: builder.mutation<
        CreateAiAgentsTestVersionResponse,
        CreateAiAgentsTestVersionApiArg
      >({
        query: ({ id, chatbotVersionId }) => ({
          version: 1,
          path: `ai-agents/${id}/test-version`,
          method: 'PUT',
          body: {
            chatbotVersionId,
          },
        }),
        transformResponse: (
          response: unknown,
        ): CreateAiAgentsTestVersionResponse => ({
          data: response as CreateAiAgentsTestVersionResponse['data'],
          meta: {},
        }),
      }),
      readAiAgents: builder.query<ReadAiAgentsApiResponse, ReadAiAgentsApiArg>({
        query: ({ id, chatbotVersionId }) => ({
          version: 1,
          path: `ai-agents/${id}`,
          params: { chatbotVersionId },
        }),
        transformResponse: (response: unknown): ReadAiAgentsApiResponse => ({
          data: response as ReadAiAgentsApiResponse['data'],
          meta: {},
        }),
        providesTags: ['AiAgent'],
      }),
      readAiAgentsList: builder.query<
        ReadAiAgentsListApiResponse,
        ReadAiAgentsListApiArg
      >({
        query: () => ({
          version: 1,
          path: 'ai-agents',
        }),
        transformResponse: (
          response: unknown,
        ): ReadAiAgentsListApiResponse => ({
          data: response as ReadAiAgentsListApiResponse['data'],
          meta: {},
        }),
        providesTags: ['AiAgents'],
      }),
      readAiAgentsKnowledgeBases: builder.query<
        ReadAiAgentsKnowledgeBasesApiResponse,
        ReadAiAgentsKnowledgeBasesApiArg
      >({
        query: ({ id }) => ({
          version: 1,
          path: `ai-agents/${id}/knowledge-bases`,
        }),
        transformResponse: (
          response: unknown,
        ): ReadAiAgentsKnowledgeBasesApiResponse => ({
          data: response as ReadAiAgentsKnowledgeBasesApiResponse['data'],
          meta: {},
        }),
      }),
      updateAiAgents: builder.mutation<
        UpdateAiAgentsApiResponse,
        UpdateAiAgentsApiArg
      >({
        query: ({ id, ...rest }) => ({
          version: 1,
          path: `ai-agents/${id}`,
          method: 'PATCH',
          body: rest,
        }),
        transformResponse: (response: unknown): UpdateAiAgentsApiResponse => ({
          data: response as UpdateAiAgentsApiResponse['data'],
          meta: {},
        }),
        invalidatesTags: ['AiAgents', 'AiAgent'],
      }),
      deleteAiAgents: builder.mutation<void, DeleteAiAgentsApiArg>({
        query: ({ id }) => ({
          version: 1,
          path: `ai-agents/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['AiAgents'],
      }),
      createAiAgentsDuplicate: builder.mutation<
        void,
        CreateAiAgentsDuplicateApiArg
      >({
        query: ({ id }) => ({
          version: 1,
          path: `ai-agents/${id}/duplicate`,
          method: 'POST',
        }),
        invalidatesTags: ['AiAgents'],
      }),
      readAiAgentsConfigCommits: builder.query<
        ReadAiAgentsConfigCommitsApiResponse,
        ReadAiAgentsConfigCommitsApiArg
      >({
        query: ({ id }) => ({
          version: 1,
          path: `ai-agents/${id}/config-commits`,
        }),
        transformResponse: (
          response: unknown,
        ): ReadAiAgentsConfigCommitsApiResponse => ({
          data: response as ReadAiAgentsConfigCommitsApiResponse['data'],
          meta: {},
        }),
        providesTags: ['AiAgentConfigCommits'],
      }),
      createAiAgentsConfigCommits: builder.mutation<
        CreateAiAgentsConfigCommitsApiResponse,
        CreateAiAgentsConfigCommitsApiArg
      >({
        query: ({ id, chatbotVersionId, name }) => ({
          version: 1,
          path: `ai-agents/${id}/config-commits`,
          method: 'POST',
          body: { chatbotVersionId, name },
        }),
        transformResponse: (
          response: unknown,
        ): CreateAiAgentsConfigCommitsApiResponse => ({
          data: response as CreateAiAgentsConfigCommitsApiResponse['data'],
          meta: {},
        }),
        invalidatesTags: ['AiAgentConfigCommits'],
      }),
      updateAiAgentsConfigCommit: builder.mutation<
        UpdateAiAgentsConfigCommitApiResponse,
        UpdateAiAgentsConfigCommitApiArg
      >({
        query: ({ id, chatbotVersionId, name }) => ({
          version: 1,
          path: `ai-agents/${id}/config-commits`,
          method: 'PATCH',
          body: { chatbotVersionId, name },
        }),
        transformResponse: (
          response: unknown,
        ): UpdateAiAgentsConfigCommitApiResponse => ({
          data: response as UpdateAiAgentsConfigCommitApiResponse['data'],
          meta: {},
        }),
        invalidatesTags: ['AiAgentConfigCommits'],
      }),
      createAiAgentsDeployments: builder.mutation<
        void,
        CreateAiAgentsDeploymentsApiArg
      >({
        query: ({ id, chatbotVersionId }) => ({
          version: 1,
          path: `ai-agents/${id}/deployments`,
          method: 'POST',
          body: { chatbotVersionId },
        }),
        invalidatesTags: ['AiAgentConfigCommits'],
      }),
      createAiAgentsVersionRestore: builder.mutation<
        CreateAiAgentsVersionRestoreApiResponse,
        CreateAiAgentsVersionRestoreApiArg
      >({
        query: ({ id, versionId, chatbotVersionId }) => ({
          version: 1,
          path: `ai-agents/${id}/versions/${versionId}/restore`,
          method: 'POST',
          body: { chatbotVersionId },
        }),
        invalidatesTags: [
          'AiAgent',
          'AiAgents',
          'AiAgentConfigCommits',
          'AiAgentTools',
        ],
      }),
      readAiAgentsTools: builder.query<
        ReadAiAgentsToolsApiResponse,
        ReadAiAgentsToolsApiArg
      >({
        query: ({ id, offset, limit, type, chatbotVersionId }) => ({
          version: 1,
          path: `ai-agents/${id}/tools`,
          params: { offset, limit, type, chatbotVersionId },
        }),
        transformResponse: (
          response: unknown,
        ): ReadAiAgentsToolsApiResponse => ({
          data: response as ReadAiAgentsToolsApiResponse['data'],
          meta: {},
        }),
        providesTags: ['AiAgentTools'],
      }),
      readAiAgentsTool: builder.query<
        ReadAiAgentsToolApiResponse,
        ReadAiAgentsToolApiArg
      >({
        query: ({ id, toolId, chatbotVersionId }) => ({
          version: 1,
          path: `ai-agents/${id}/tools/${toolId}`,
          params: { chatbotVersionId },
        }),
        transformResponse: (
          response: unknown,
        ): ReadAiAgentsToolApiResponse => ({
          data: response as ReadAiAgentsToolApiResponse['data'],
          meta: {},
        }),
        providesTags: ['AiAgentTool'],
      }),
      // Tools (all types)
      createAiAgentsTool: builder.mutation<
        CreateAiAgentsToolApiResponse,
        CreateAiAgentsToolApiArg
      >({
        query: ({ id, ...toolData }) => ({
          version: 1,
          path: `ai-agents/${id}/tools`,
          method: 'POST',
          body: toolData,
        }),
        invalidatesTags: ['AiAgentTools', 'AiAgent'],
      }),
      createAiAgentsToolsById: builder.mutation<
        CreateAiAgentsToolsByIdApiResponse,
        CreateAiAgentsToolsByIdApiArg
      >({
        query: ({ id, toolId, ...toolData }) => ({
          version: 1,
          path: `ai-agents/${id}/tools/${toolId}`,
          method: 'PUT',
          body: toolData,
        }),
        invalidatesTags: ['AiAgentTools', 'AiAgentTool', 'AiAgent'],
      }),
      deleteAiAgentsTool: builder.mutation<
        DeleteAiAgentsToolApiResponse,
        DeleteAiAgentsToolApiArg
      >({
        query: ({ id, toolId }) => ({
          version: 1,
          path: `ai-agents/${id}/tools/${toolId}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['AiAgentTools', 'AiAgent'],
      }),
      createAiAgentsToolDuplicate: builder.mutation<
        void,
        CreateAiAgentsToolDuplicateApiArg
      >({
        query: ({ id, toolId }) => ({
          version: 1,
          path: `ai-agents/${id}/tools/${toolId}/duplicate`,
          method: 'POST',
        }),
        invalidatesTags: ['AiAgentTools', 'AiAgent'],
      }),
      readAiAgentsToolReferences: builder.query<
        ReadAiAgentsToolReferencesApiResponse,
        ReadAiAgentsToolReferencesApiArg
      >({
        query: ({ id, toolId }) => ({
          version: 1,
          path: `ai-agents/${id}/tools/${toolId}/references`,
        }),
        transformResponse: (
          response: unknown,
        ): ReadAiAgentsToolReferencesApiResponse => ({
          data: response as ReadAiAgentsToolReferencesApiResponse['data'],
          meta: {},
        }),
        providesTags: ['AiAgentTools'],
      }),
      readToolsIntegrationsActions: builder.query<
        ReadToolsIntegrationsActionsApiResponse,
        ReadToolsIntegrationsActionsApiArg
      >({
        query: ({ integrationType }) => ({
          version: 1,
          path: `tools/integrations/${integrationType}/actions`,
        }),
      }),
      createToolsIntegrationsActionsRun: builder.query<
        CreateToolsIntegrationsActionsRunApiResponse,
        CreateToolsIntegrationsActionsRunApiArg
      >({
        query: ({ integrationType, actionId, csid, configuredParameters }) => ({
          version: 1,
          path: `tools/integrations/${integrationType}/actions/${actionId}/run`,
          method: 'POST',
          params: !!csid ? { csid } : undefined,
          body: configuredParameters
            ? Object.entries(configuredParameters).map(([name, value]) => ({
                name,
                value: { value },
              }))
            : [],
        }),
      }),
      readAiAgentsDefaults: builder.query<
        ReadAiAgentsDefaultsApiResponse,
        void
      >({
        query: () => ({
          version: 1,
          path: 'ai-agents/defaults',
        }),
        transformResponse: (
          response: unknown,
        ): ReadAiAgentsDefaultsApiResponse => ({
          data: response as ReadAiAgentsDefaultsApiResponse['data'],
          meta: {},
        }),
      }),
      readChatbotsQuotasV2: builder.query<
        ReadChatbotsQuotasV2ApiResponse,
        ReadChatbotsQuotasV2ApiArg
      >({
        query: ({ year, month, feature }) => ({
          version: 2,
          path: 'chatbots/quotas',
          params: { year, month, feature },
        }),
        providesTags: ['ChatbotQuotas'],
      }),
      updateChatbotsQuotas: builder.mutation<
        UpdateChatbotsQuotasApiResponse,
        UpdateChatbotsQuotasApiArg
      >({
        query: ({ feature, extraLimit }) => ({
          version: 1,
          path: `chatbots/quotas/${feature}/extra-limit`,
          method: 'PUT',
          body: { extraLimit },
        }),
        invalidatesTags: ['ChatbotQuotas'],
      }),
      createToolsDynamicSchemaRun: builder.query<
        CreateToolsDynamicSchemaRunApiResponse,
        CreateToolsDynamicSchemaRunApiArg
      >({
        query: ({ resolverId, value, name }) => ({
          version: 1,
          path: `tools/dynamic-schema/${resolverId}/run`,
          method: 'POST',
          body: { value, ...(name && { name }) },
        }),
      }),
      runStreamAiAgent: builder.query<
        AiAgentRunStreamResponse,
        {
          agentId: string;
          dixaConversationId: string;
          userId: string;
        }
      >({
        queryFn: () =>
          ({
            data: {
              text: '',
              meta: {
                isLoading: true,
                isStreaming: false,
                isError: false,
                isSuccess: false,
              },
              feedback_id: '',
            },
          }) as MaybePromise<
            QueryReturnValue<AiAgentRunStreamResponse, QueryError>
          >,
        async onQueryStarted(
          { agentId, dixaConversationId, userId },
          { updateCachedData },
        ) {
          const conversationIdNum = Number(dixaConversationId);
          if (isNaN(conversationIdNum)) {
            updateCachedData((draft) => {
              draft.meta.isLoading = false;
              draft.meta.isError = true;
            });
            return;
          }

          await streamUpdates<AiAgentRunStreamResponse>(
            updateCachedData,
            {
              url: `${config.url.rest}/v1/ai-agents/${agentId}/run`,
              body: {
                userId,
                dixaConversationId: String(dixaConversationId),
                session: {
                  _type: 'dixa-conversation-id',
                  id: conversationIdNum,
                },
                waitForUserInput: false,
                addMessagesToConversation: false,
                options: {
                  autoTaggingEnabled: false,
                  overpromiseDetectionEnabled: false,
                },
              } satisfies RunAiAgentRequest,
              headers: {
                Authorization: await createAuthorizationHeaderValue(),
                Accept: 'text/event-stream',
              },
            },
            createAiAgentMessageAppender(),
          );
        },
        keepUnusedDataFor: 0,
      }),
      /** The non-streaming counterpart to `runStreamAiAgent`: resolves once with the finished text. */
      createAiAgentDraft: builder.mutation<
        { text: string },
        { agentId: string; dixaConversationId: string; userId: string }
      >({
        queryFn: async (
          { agentId, dixaConversationId, userId },
          { signal },
        ) => {
          const conversationIdNum = Number(dixaConversationId);
          if (isNaN(conversationIdNum)) {
            return {
              error: {
                status: ApiErrorStatusCode.BadRequest,
                data: 'Invalid conversation id',
              } satisfies QueryError,
            };
          }

          const draft: AiAgentDraftTarget = { text: '' };
          const appendMessage = createAiAgentMessageAppender();
          let hasError = false;

          await streamReader({
            url: `${config.url.rest}/v1/ai-agents/${agentId}/run`,
            body: {
              userId,
              dixaConversationId: String(dixaConversationId),
              session: { _type: 'dixa-conversation-id', id: conversationIdNum },
              waitForUserInput: false,
              addMessagesToConversation: false,
              options: {
                autoTaggingEnabled: false,
                overpromiseDetectionEnabled: false,
              },
            } satisfies RunAiAgentRequest,
            headers: {
              Authorization: await createAuthorizationHeaderValue(),
              Accept: 'text/event-stream',
            },
            signal,
            onChunk: (chunk) => appendMessage(draft, chunk),
            onDone: () => {},
            onError: () => {
              hasError = true;
            },
          });

          if (hasError || draft.failureReason) {
            return {
              error: {
                status: ApiErrorStatusCode.InternalServerError,
                data: draft.failureReason
                  ? getMimDraftFailureMessage(draft.failureReason)
                  : 'Failed to generate draft',
              } satisfies QueryError,
            };
          }

          return { data: draft };
        },
      }),
      createAiAgentsSidekickConversation: builder.mutation<
        CreateAiAgentsSidekickConversationApiResponse,
        CreateAiAgentsSidekickConversationApiArg
      >({
        query: (body) => ({
          version: 1,
          path: 'ai-agents/sidekick/conversations',
          method: 'POST',
          body,
        }),
        transformResponse: (
          response: unknown,
        ): CreateAiAgentsSidekickConversationApiResponse => ({
          data: response as CreateAiAgentsSidekickConversationApiResponse['data'],
          meta: {},
        }),
        invalidatesTags: ['SidekickConversations'],
      }),
      createAiAgentsSidekickConversationMessages: builder.mutation<
        CreateAiAgentsSidekickConversationMessagesApiResponse,
        CreateAiAgentsSidekickConversationMessagesApiArg
      >({
        query: ({ conversationId, messages, sdkVersion }) => ({
          version: 1,
          path: `ai-agents/sidekick/conversations/${conversationId}/messages`,
          method: 'POST',
          body: { messages, sdkVersion },
        }),
        invalidatesTags: ['SidekickConversations'],
      }),
      readAiAgentsSidekickConversations: builder.query<
        ReadAiAgentsSidekickConversationsApiResponse,
        ReadAiAgentsSidekickConversationsApiArg
      >({
        query: (arg) =>
          paginatedQuery(arg, () => ({
            version: 1,
            path: 'ai-agents/sidekick/conversations',
          })),
        providesTags: ['SidekickConversations'],
      }),
      readAiAgentsSidekickConversationsMessages: builder.query<
        ReadAiAgentsSidekickConversationsMessagesApiResponse,
        ReadAiAgentsSidekickConversationsMessagesApiArg
      >({
        query: (arg) =>
          paginatedQuery(arg, ({ conversationId, anchor }) => ({
            version: 1,
            path: `ai-agents/sidekick/conversations/${conversationId}/messages`,
            params: { anchor },
          })),
      }),
    }),
  });
